import { OrganizationDateFormat } from "./shared-enums/organization-date-format.enum";

/**
 * numeric display format strings for one org date format choice
 */
export type OrganizationDateFormats = {
  date: string;
  shortYearDate: string;
  monthDay: string;
  monthYear: string;
  dateTime: string;
  dateAtTime: string;
  dateTimeWithTimezone: string;
  dateTimeWithSecondsAndTimezone: string;
  dateTimePrecise: string;
};

type DatePieces = Pick<OrganizationDateFormats, "date" | "shortYearDate" | "monthDay" | "monthYear">;

const DATE_PIECES: Record<OrganizationDateFormat, DatePieces> = {
  [OrganizationDateFormat.MM_DD_YYYY]: {
    date: "MM/dd/yyyy",
    shortYearDate: "MM/dd/yy",
    monthDay: "MM/dd",
    monthYear: "MM/yyyy",
  },
  [OrganizationDateFormat.DD_MM_YYYY]: {
    date: "dd/MM/yyyy",
    shortYearDate: "dd/MM/yy",
    monthDay: "dd/MM",
    monthYear: "MM/yyyy",
  },
  [OrganizationDateFormat.YYYY_MM_DD]: {
    date: "yyyy/MM/dd",
    shortYearDate: "yy/MM/dd",
    monthDay: "MM/dd",
    monthYear: "yyyy/MM",
  },
};

const buildFormats = (pieces: DatePieces): OrganizationDateFormats => ({
  ...pieces,
  dateTime: `${pieces.date} hh:mm a`,
  dateAtTime: `${pieces.date} @ hh:mm a`,
  dateTimeWithTimezone: `${pieces.date} hh:mm a ZZZZ`,
  dateTimeWithSecondsAndTimezone: `${pieces.date} hh:mm:ss a ZZZZ`,
  dateTimePrecise: `${pieces.date} hh:mm:ss.SSS a`,
});

type OrganizationDateFormatValue = `${OrganizationDateFormat}`;

const ORGANIZATION_DATE_FORMATS: Record<OrganizationDateFormatValue, OrganizationDateFormats> = {
  [OrganizationDateFormat.MM_DD_YYYY]: buildFormats(DATE_PIECES[OrganizationDateFormat.MM_DD_YYYY]),
  [OrganizationDateFormat.DD_MM_YYYY]: buildFormats(DATE_PIECES[OrganizationDateFormat.DD_MM_YYYY]),
  [OrganizationDateFormat.YYYY_MM_DD]: buildFormats(DATE_PIECES[OrganizationDateFormat.YYYY_MM_DD]),
};

/**
 * resolve the numeric display formats for an org date format choice
 *
 * a nil choice falls back to the american default so callers can pass an org that has not loaded yet
 *
 * accepts the literal values too so generated enum copies pass without casts
 */
export const getOrganizationDateFormats = (
  dateFormat?: OrganizationDateFormat | OrganizationDateFormatValue | null,
): OrganizationDateFormats => ORGANIZATION_DATE_FORMATS[dateFormat ?? OrganizationDateFormat.MM_DD_YYYY];
